/**
 * PrintfulMerchGrid — reusable grid of Printful products with a Buy button.
 * Two flavors:
 *   • mode="artist" + artistId → renders {artist}'s published Printful merch
 *   • mode="platform"          → renders site-branded MeloCraft merch
 * When empty (artist hasn't published anything / platform hasn't been synced yet),
 * either renders nothing (`hideIfEmpty`) or shows an empty state.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, dollars } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShoppingBag, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";

export default function PrintfulMerchGrid({ mode, artistId, hideIfEmpty = false, title = "Merch" }) {
  const [products, setProducts] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const url = mode === "artist"
      ? `/printful/artists/${artistId}/products`
      : `/printful/platform/products`;
    api.get(url)
      .then((r) => setProducts(r.data.products || []))
      .catch(() => setProducts([]));
  }, [mode, artistId]);

  if (products === null) return null;
  if (products.length === 0 && hideIfEmpty) return null;

  const buy = async (p, variant) => {
    if (!user) { toast.error("Sign in to buy merch"); nav("/login"); return; }
    const item_id = mode === "artist"
      ? `pf:artist:${artistId}:${variant.id}`
      : `pf:platform:${variant.id}`;
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "merch", item_id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <ShoppingBag className="h-5 w-5 text-[#FF4500]" />
        <h2 className="font-display text-2xl md:text-3xl">{title}</h2>
      </div>

      {products.length === 0 ? (
        <div data-testid={`pf-empty-${mode}`}
             className="border border-dashed border-[#2A2A2A] p-8 text-center font-mono text-xs text-gray-500 uppercase tracking-widest">
          {mode === "artist"
            ? "No merch yet — this artist hasn't published Printful products."
            : "Site-branded merch is coming soon. Check back after the founder drops the first design."}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {products.map((p) => {
            const variant = pickCheapestVariant(p.variants);
            const price = variant?.retail_price
              ? Math.round(parseFloat(variant.retail_price) * 100)
              : (p.price_cents || 0);
            return (
              <div key={p.printful_id} data-testid={`pf-merch-${p.printful_id}`}
                   className="border border-[#2A2A2A] bg-[#121212] hover-lift">
                <div className="aspect-square overflow-hidden bg-neutral-900">
                  {p.thumbnail_url
                    ? <img src={p.thumbnail_url} alt={p.name} className="h-full w-full object-cover" />
                    : <div className="h-full w-full grid place-items-center text-gray-600"><ShoppingBag className="h-10 w-10" /></div>}
                </div>
                <div className="p-4">
                  <div className="font-display font-bold truncate">{p.name || "Merch"}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                    {p.variants?.length || 1} option{(p.variants?.length || 1) === 1 ? "" : "s"}
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-mono text-sm text-[#FF4500]">{dollars(price)}</span>
                    <Button
                      data-testid={`buy-pf-${p.printful_id}`}
                      onClick={() => variant && buy(p, variant)}
                      disabled={!variant}
                      className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-8 px-3"
                    >
                      <ShoppingCart className="h-3 w-3 mr-1" /> Buy
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function pickCheapestVariant(variants) {
  if (!variants || !variants.length) return null;
  return variants.reduce((min, v) => {
    const price = parseFloat(v.retail_price || 0);
    if (!min) return v;
    const minPrice = parseFloat(min.retail_price || 0);
    return price > 0 && (price < minPrice || minPrice <= 0) ? v : min;
  }, null);
}
